import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { OrderLocation } from '@ml/orders/types/classes';
import { ConfirmDialogComponent } from '@ml/shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CustomerLocationModalComponent } from '@ml/shared/components/customer-location-modal/customer-location-modal.component';
import { Location } from '@ml/locations/types';
import { GoogleMapsService } from '@ml/shared/services/google-maps.service';
import { Task } from '@ml/tasks/types/classes';
import { TaskLocation } from '@ml/tasks/types/classes/task-location.class';
import { User } from '@ml/core/types/classes';
import { OrderLocationStatusDialogComponent } from '@ml/shared/containers/order-location-status-dialog/order-location-status-dialog.component';
import { UpdateResourcePosition } from '@ml/shared/types/interfaces';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { UpdateTaskPosition } from '@ml/orders/types/update-task-position';
import { Order, OrderStatus } from '@ml/orders/types/order';
import { MediaObserver } from '@angular/flex-layout';
import { LocationsMoveDialogComponent } from '../locations-move-dialog/locations-move-dialog.component';

@Component({
  selector: 'ml-order-locations-table',
  template: `
    <mat-card fxFlex>
      <ml-table-header
        [title]="'locations'"
        [titleIcon]="'location'"
        [canAdd]="false"
      >
        <div *ngIf="canAdd && canAddLocation && connection">
          <ml-add-button [matMenuTriggerFor]="menu"></ml-add-button>
          <mat-menu #menu="matMenu">
            <button mat-menu-item (click)="onAddCustomerLocation()">
              {{ 'customer-location' | translate }}
            </button>
            <button mat-menu-item (click)="onAddManualLocation()">
              {{ 'manual-location' | translate }}
            </button>
          </mat-menu>
        </div>
      </ml-table-header>
      <table
        class="full-width"
        mat-table
        [dataSource]="orderLocations || []"
        cdkDropList
        [cdkDropListData]="orderLocations"
        (cdkDropListDropped)="dropTable($event)"
        [cdkDropListDisabled]="!canDrag"
      >
        <ng-container matColumnDef="id">
          <th mat-header-cell *matHeaderCellDef>#</th>
          <td mat-cell *matCellDef="let element">
            {{ element.position + 1 }}
          </td>
        </ng-container>

        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'name' | translate | titlecase }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.location.name }}
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'status' | translate | titlecase }}
          </th>
          <td mat-cell *matCellDef="let element">
            <ml-order-location-status
              [user]="user"
              [currentTask]="task"
              [orderLocation]="element"
              [tasks]="getTasks(element)"
              (selectLocation)="onSelectLocation($event)"
            ></ml-order-location-status>
          </td>
        </ng-container>

        <ng-container matColumnDef="preview">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'preview' | translate | titlecase }}
          </th>
          <td mat-cell *matCellDef="let element">
            <button
              [disabled]="!connection"
              mat-icon-button
              color="primary"
              (click)="onPreview(element)"
            >
              <mat-icon svgIcon="view"></mat-icon>
            </button>
          </td>
        </ng-container>

        <ng-container matColumnDef="navigation">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'navigation' | translate | titlecase }}
          </th>
          <td mat-cell *matCellDef="let element">
            <button
              [disabled]="!connection"
              mat-icon-button
              (click)="onNavigation(element)"
            >
              <mat-icon svgIcon="location"></mat-icon>
            </button>
          </td>
        </ng-container>

        <ng-container matColumnDef="move">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'move' | translate | titlecase }}
          </th>
          <td mat-cell *matCellDef="let element">
            <button
              [disabled]="!connection"
              mat-icon-button
              (click)="onMove(element)"
            >
              <mat-icon svgIcon="sort"></mat-icon>
            </button>
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let element" class="action-column">
            <button
              mat-icon-button
              [matMenuTriggerFor]="menu"
              (click)="$event.stopPropagation()"
            >
              <mat-icon svgIcon="more_vert"></mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button
                [disabled]="!connection"
                mat-menu-item
                (click)="onEdit(element)"
              >
                <mat-icon matPrefix svgIcon="edit"></mat-icon>
                {{ 'edit' | translate | titlecase }}
              </button>
              <button
                [disabled]="!connection"
                mat-menu-item
                (click)="onRemove(element)"
              >
                <mat-icon matPrefix svgIcon="delete" color="warn"></mat-icon>
                {{ 'remove' | translate | titlecase }}
              </button>
            </mat-menu>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>

        <tr
          mat-row
          [ngClass]="{ 'cursor-pointer': canDrag }"
          style="width: 100%"
          *matRowDef="let row; columns: displayedColumns"
          cdkDrag
          cdkDragLockAxis="y"
          [cdkDragData]="row"
        ></tr>
      </table>

      <ml-empty-table
        [data]="orderLocations"
        [loading]="loading"
        [message]="'no-locations'"
      ></ml-empty-table>
      <div
        *ngIf="loading"
        fxLayout
        fxLayoutAlign="center center"
        class="padding-16"
      >
        <mat-spinner diameter="48"></mat-spinner>
      </div>
    </mat-card>
  `,
  styleUrls: ['./order-locations-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderLocationsTableComponent implements OnInit, AfterViewChecked {
  @Input() user: User;
  @Input() task: Task;
  @Input() order: Order;
  @Input() tasks: Task[];
  @Input() orderLocations: OrderLocation[];
  @Input() loading: boolean;
  @Input() canAdd: boolean;
  @Input() canEdit = true;
  @Input() canDrag = false;
  @Input() connection: boolean;

  @Output() add = new EventEmitter();
  @Output() addCustomerLocations = new EventEmitter();
  @Output() addManualLocation = new EventEmitter();
  @Output() editLocation = new EventEmitter();
  @Output() remove = new EventEmitter<OrderLocation>();
  @Output() updatePositions = new EventEmitter<UpdateResourcePosition[]>();

  displayedColumns = ['id', 'name', 'status', 'preview', 'navigation'];

  constructor(
    private dialog: MatDialog,
    private googleMapsService: GoogleMapsService,
    private cdr: ChangeDetectorRef,
    private mediaObserver: MediaObserver,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.canDrag) {
      this.displayedColumns.push('move');
    }
    if (this.canEdit) {
      this.displayedColumns.push('actions');
    }
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  get canAddLocation() {
    return (
      this.order &&
      this.order.status !== OrderStatus.READY_ACCOUNTING &&
      this.order.status !== OrderStatus.INVOICE_ISSUED &&
      this.order.status !== OrderStatus.PAID &&
      this.order.status !== OrderStatus.CANCELLED
    );
  }

  dropTable(event: CdkDragDrop<OrderLocation[]>) {
    const prevIndex = this.orderLocations.findIndex(
      d => d.id === event.item.data.id
    );
    const orderLocations = [...this.orderLocations];
    moveItemInArray(orderLocations, prevIndex, event.currentIndex);

    this.orderLocations = [...orderLocations];

    const updatePositions: UpdateTaskPosition[] = this.orderLocations.map(
      (orderLocation, index) => ({
        id: orderLocation.id,
        position: index
      })
    );
    this.updatePositions.emit(updatePositions);
  }

  onSelectLocation(taskLocation: TaskLocation) {
    if (!this.connection) {
      return;
    }
    const task = this.tasks.find(value => value.id === taskLocation.task);
    this.dialog
      .open(OrderLocationStatusDialogComponent, {
        width: '400px',
        maxWidth: '100vw',
        data: {
          task: task,
          taskLocation
        }
      })
      .afterClosed()
      .subscribe((_taskLocation: TaskLocation) => {
        this.tasks = [...this.tasks];
        this.cdr.detectChanges();
      });
  }

  onEdit(orderLocation: OrderLocation) {
    const isTablet = this.mediaObserver.isActive('lg');
    this.matDialog
      .open(CustomerLocationModalComponent, {
        width: isTablet ? '800px' : '100vw',
        maxWidth: '100vw',
        data: {
          location: orderLocation.location
        }
      })
      .afterClosed()
      .subscribe(_value => {
        orderLocation.location = _value;
        this.cdr.detectChanges();
        if (_value) {
          this.editLocation.emit(_value);
        }
      });
  }

  getTasks(orderLocation: OrderLocation) {
    if (!this.tasks) {
      return [];
    }
    return this.tasks.filter(task => {
      const locations = task.locations as TaskLocation[];
      return (
        locations.findIndex(value => {
          if (typeof value.orderLocation === 'number') {
            return value.orderLocation === orderLocation.id;
          }
          return value.orderLocation.id === orderLocation.id;
        }) >= 0
      );
    });
  }

  onAddManualLocation() {
    this.addManualLocation.emit();
  }

  onAddCustomerLocation() {
    this.addCustomerLocations.emit();
  }

  onAdd() {
    this.add.emit();
  }

  onPreview(orderLocation: OrderLocation) {
    const isTablet = this.mediaObserver.isActive('lg');
    this.dialog.open(CustomerLocationModalComponent, {
      width: isTablet ? '800px' : '100vw',
      maxWidth: '100vw',
      data: {
        location: orderLocation.location,
        disable: true
      }
    });
  }

  onNavigation(orderLocation: OrderLocation) {
    const location = orderLocation.location as Location;
    this.googleMapsService.navigateTo(location);
  }

  onMove(orderLocation: OrderLocation) {
    const prevIndex = this.orderLocations.findIndex(
      d => d.id === orderLocation.id
    );

    const orderLocations = [...this.orderLocations];

    const isTablet = this.mediaObserver.isActive('lg');
    const dialogRef = this.dialog.open(LocationsMoveDialogComponent, {
      width: isTablet ? '800px' : '100vw',
      maxWidth: '100vw',
      data: {
        orderLocationToMove: orderLocation,
        orderLocations: this.orderLocations
      }
    });

    dialogRef.afterClosed().subscribe((currentIndex?: number) => {
      if (currentIndex === undefined) {
        return;
      }
      moveItemInArray(orderLocations, prevIndex, currentIndex);

      this.orderLocations = [...orderLocations];

      const updatePositions: UpdateTaskPosition[] = this.orderLocations.map(
        (newOrderLocation, index) => ({
          id: newOrderLocation.id,
          position: index
        })
      );
      this.updatePositions.emit(updatePositions);
    });
  }

  onRemove(location: OrderLocation) {
    const isTablet = this.mediaObserver.isActive('lg');
    this.dialog
      .open(ConfirmDialogComponent, {
        width: isTablet ? '800px' : '100vw',
        maxWidth: '100vw',
        data: {
          title: `confirm-remove-location`
        }
      })
      .afterClosed()
      .subscribe(accept => {
        if (accept) {
          this.remove.emit(location);
        }
      });
  }
}
